import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Zap, ArrowLeft, CheckCircle2, Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

const connectorOptions = ["Typ 1", "Typ 2", "CCS", "CHAdeMO", "Schuko", "Weiß nicht"];

const weekdays = [
  { key: "mo", label: "Mo" },
  { key: "di", label: "Di" },
  { key: "mi", label: "Mi" },
  { key: "do", label: "Do" },
  { key: "fr", label: "Fr" },
  { key: "sa", label: "Sa" },
  { key: "so", label: "So" },
];

const timeOptions = [
  "06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00",
  "20:00", "21:00", "22:00", "23:00",
];

type DaySchedule = { day: string; from: string; to: string };


const hostSchema = z.object({
  name: z.string().trim().min(2, "Name muss mindestens 2 Zeichen haben").max(100),
  email: z.string().trim().email("Bitte gültige E-Mail-Adresse eingeben").max(255),
  phone: z.string().trim().min(5, "Bitte gültige Telefonnummer eingeben").max(30),
  address: z.string().trim().min(3, "Bitte Adresse/Ort eingeben").max(200),
  connector_types: z.array(z.string()).min(1, "Bitte mindestens einen Steckertyp wählen"),
  power_kw: z.string().optional(),
  has_wallbox: z.boolean(),
  usage_mode: z.enum(["instant", "request", "later"]),
  motivation: z.string().max(1000).optional(),
  newsletter_opt_in: z.boolean(),
  privacy_accepted: z.literal(true, { errorMap: () => ({ message: "Bitte Datenschutzerklärung akzeptieren" }) }),
});

const HostSignup = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    connector_types: [] as string[],
    power_kw: "",
    has_wallbox: true,
    usage_mode: "later" as "instant" | "request" | "later",
    motivation: "",
    newsletter_opt_in: false,
    privacy_accepted: false as boolean,
    availability_schedule: [] as DaySchedule[],
  });

  const update = (field: string, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const toggleConnector = (type: string) => {
    setForm((f) => {
      const current = f.connector_types;
      if (type === "Weiß nicht") {
        return { ...f, connector_types: current.includes(type) ? [] : [type] };
      }
      const without = current.filter((t) => t !== "Weiß nicht");
      return {
        ...f,
        connector_types: without.includes(type)
          ? without.filter((t) => t !== type)
          : [...without, type],
      };
    });
  };

  const toggleDay = (dayKey: string) => {
    setForm((f) => {
      const schedule = f.availability_schedule;
      const exists = schedule.find((s) => s.day === dayKey);
      if (exists) {
        return { ...f, availability_schedule: schedule.filter((s) => s.day !== dayKey) };
      }
      return { ...f, availability_schedule: [...schedule, { day: dayKey, from: "08:00", to: "18:00" }] };
    });
  };

  const updateDayTime = (dayKey: string, field: "from" | "to", value: string) => {
    setForm((f) => ({
      ...f,
      availability_schedule: f.availability_schedule.map((s) =>
        s.day === dayKey ? { ...s, [field]: value } : s
      ),
    }));
  };
  const handlePhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = 5 - photos.length;
    const toAdd = files.slice(0, remaining);
    if (files.length > remaining) toast.info(`Maximal 5 Fotos erlaubt.`);

    setPhotos((p) => [...p, ...toAdd]);
    toAdd.forEach((f) => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews((p) => [...p, ev.target?.result as string]);
      reader.readAsDataURL(f);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (idx: number) => {
    setPhotos((p) => p.filter((_, i) => i !== idx));
    setPreviews((p) => p.filter((_, i) => i !== idx));
  };

  const uploadPhotos = async (): Promise<string[]> => {
    const paths: string[] = [];
    for (const file of photos) {
      const ext = file.name.split(".").pop();
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("host-photos").upload(path, file);
      if (!error) {
        const { data } = supabase.storage.from("host-photos").getPublicUrl(path);
        paths.push(data.publicUrl);
      }
    }
    return paths;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = hostSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }

    setLoading(true);
    const now = new Date().toISOString();

    let photoPaths: string[] = [];
    if (photos.length > 0) {
      photoPaths = await uploadPhotos();
    }

    const { error } = await supabase.from("pre_launch_hosts").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone,
      address: parsed.data.address,
      connector_type: parsed.data.connector_types,
      power_kw: parsed.data.power_kw ? Number(parsed.data.power_kw) : null,
      has_wallbox: parsed.data.has_wallbox,
      usage_mode: parsed.data.usage_mode,
      motivation: parsed.data.motivation || null,
      newsletter_opt_in: parsed.data.newsletter_opt_in,
      privacy_accepted_at: now,
      photos: photoPaths,
      availability_schedule: form.availability_schedule.length > 0 ? form.availability_schedule : null,
    } as any);

    if (!error && parsed.data.newsletter_opt_in) {
      await supabase.from("newsletter_subscribers").insert({
        email: parsed.data.email,
        name: parsed.data.name,
        source: "host",
      });
    }

    setLoading(false);

    if (error) {
      toast.error("Fehler beim Speichern. Bitte versuche es erneut.");
      return;
    }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
        <div className="mx-auto max-w-md text-center space-y-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Du bist dabei! 🎉</h1>
          <p className="text-muted-foreground leading-relaxed">
            Willkommen im Netzwerk der Ersten. Du gehörst jetzt zu den Pionieren in deiner Region.
            Wir melden uns persönlich bei dir, sobald es losgeht.
          </p>
          <div className="rounded-xl border border-primary/15 bg-primary/5 p-4 text-sm text-primary">
            <strong>Tipp:</strong> Teile chargeflip mit Nachbarn, die auch eine Wallbox haben — je mehr Ladepunkte in deiner Region, desto schneller starten wir bei euch.
          </div>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Zurück zur Startseite
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-lg py-8">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-6 gap-2">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Button>
        </Link>

        <div className="mb-8">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Zap className="h-5 w-5 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Jetzt kostenlos Platz sichern</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            In 2 Minuten angemeldet — kein Risiko, keine Kosten, jederzeit pausierbar.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {["✓ Steuerfrei", "✓ Versichert", "✓ Kein Gewerbe"].map((tag) => (
              <span key={tag} className="rounded-full bg-primary/5 px-2.5 py-0.5 text-xs font-medium text-primary">{tag}</span>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name *</Label>
            <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Max Mustermann" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">E-Mail *</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="max@beispiel.de" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">Telefonnummer *</Label>
            <Input id="phone" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+49 170 1234567" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="address">Adresse / Ort *</Label>
            <Input id="address" value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Musterstraße 1, 12345 Musterort" required />
          </div>

          {/* Multi-select connector types */}
          <div className="space-y-2">
            <Label>Steckertyp(en) * <span className="font-normal text-muted-foreground">(Mehrfachauswahl möglich)</span></Label>
            <div className="flex flex-wrap gap-2">
              {connectorOptions.map((type) => {
                const selected = form.connector_types.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleConnector(type)}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="power">Ladeleistung in kW (optional)</Label>
            <Input id="power" type="number" min={1} max={350} value={form.power_kw} onChange={(e) => update("power_kw", e.target.value)} placeholder="z.B. 11" />
          </div>

          <div className="space-y-2">
            <Label>Wallbox vorhanden? *</Label>
            <RadioGroup
              value={form.has_wallbox ? "yes" : "no"}
              onValueChange={(v) => update("has_wallbox", v === "yes")}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="wb-yes" />
                <Label htmlFor="wb-yes" className="font-normal">Ja</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="wb-no" />
                <Label htmlFor="wb-no" className="font-normal">Nein</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Nutzungsart *</Label>
            <RadioGroup
              value={form.usage_mode}
              onValueChange={(v) => update("usage_mode", v)}
              className="space-y-2"
            >
              {[
                { value: "instant", label: "Sofort verfügbar" },
                { value: "request", label: "Auf Anfrage" },
                { value: "later", label: "Später entscheiden" },
              ].map((o) => (
                <div key={o.value} className="flex items-center gap-2">
                  <RadioGroupItem value={o.value} id={`mode-${o.value}`} />
                  <Label htmlFor={`mode-${o.value}`} className="font-normal">{o.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Availability schedule */}
          <div className="space-y-3">
            <Label>Verfügbarkeit <span className="font-normal text-muted-foreground">(optional — an welchen Tagen und zu welchen Zeiten?)</span></Label>
            <div className="flex flex-wrap gap-2">
              {weekdays.map((wd) => {
                const active = form.availability_schedule.some((s) => s.day === wd.key);
                return (
                  <button
                    key={wd.key}
                    type="button"
                    onClick={() => toggleDay(wd.key)}
                    className={`h-10 w-10 rounded-lg border text-sm font-medium transition-colors ${
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}
                  >
                    {wd.label}
                  </button>
                );
              })}
            </div>
            {form.availability_schedule.length > 0 && (
              <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
                {weekdays
                  .filter((wd) => form.availability_schedule.some((s) => s.day === wd.key))
                  .map((wd) => {
                    const entry = form.availability_schedule.find((s) => s.day === wd.key)!;
                    return (
                      <div key={wd.key} className="flex items-center gap-2 text-sm">
                        <span className="w-8 font-medium text-foreground">{wd.label}</span>
                        <select
                          value={entry.from}
                          onChange={(e) => updateDayTime(wd.key, "from", e.target.value)}
                          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                        >
                          {timeOptions.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        <span className="text-muted-foreground">bis</span>
                        <select
                          value={entry.to}
                          onChange={(e) => updateDayTime(wd.key, "to", e.target.value)}
                          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                        >
                          {timeOptions.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>


          <div className="space-y-2">
            <Label>Fotos deiner Wallbox / Ladestation (optional, max. 5)</Label>
            <div className="flex flex-wrap gap-3">
              {previews.map((src, i) => (
                <div key={i} className="relative h-20 w-20 overflow-hidden rounded-lg border border-border">
                  <img src={src} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-foreground/70 text-background transition-colors hover:bg-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  <ImageIcon className="h-5 w-5" />
                  <span className="text-[10px]">Hinzufügen</span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handlePhotos}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="motivation">Was motiviert dich? (optional)</Label>
            <Textarea
              id="motivation"
              value={form.motivation}
              onChange={(e) => update("motivation", e.target.value)}
              placeholder="z.B. Ich möchte meine Wallbox in der Nachbarschaft teilen…"
              rows={3}
            />
          </div>

          {/* Newsletter + DSGVO */}
          <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="newsletter"
                checked={form.newsletter_opt_in}
                onCheckedChange={(v) => update("newsletter_opt_in", !!v)}
                className="mt-0.5"
              />
              <Label htmlFor="newsletter" className="font-normal text-sm leading-relaxed cursor-pointer">
                Ja, ich möchte den chargeflip-Newsletter erhalten und über Neuigkeiten, den Launch und Tipps informiert werden. Abmeldung jederzeit möglich.
              </Label>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="privacy"
                checked={form.privacy_accepted}
                onCheckedChange={(v) => update("privacy_accepted", !!v)}
                className="mt-0.5"
              />
              <Label htmlFor="privacy" className="font-normal text-sm leading-relaxed cursor-pointer">
                Ich habe die{" "}
                <a href="/datenschutz" target="_blank" className="text-primary underline hover:no-underline">
                  Datenschutzerklärung
                </a>{" "}
                gelesen und stimme der Verarbeitung meiner Daten zu. *
              </Label>
            </div>
          </div>

          <Button type="submit" className="w-full" size="lg" disabled={loading || !form.privacy_accepted}>
            {loading ? "Wird gespeichert…" : "Ladepunkt registrieren"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default HostSignup;
